import React from 'react';
import { useEvidencePanel, EvidenceItem } from './EvidencePanelContext';

const TOOL_LABELS: Record<string, string> = {
  hts_lookup: 'HTS Database',
  cross_search: 'CROSS Rulings',
  web_search: 'Web Search',
  ask_user: 'User Input',
  tariff_impact: 'Tariff Impact',
};

/* ── HTS results table ────────────────────────────────────────────────────── */

const HTSResultsTable: React.FC<{ data: any }> = ({ data }) => {
  const results = data.results || [];
  const total = data.total_matched || results.length;
  const returned = data.returned || results.length;

  if (results.length === 0) {
    return (
      <div className="text-sm text-gray-500 px-4 py-3">
        No matching codes found.
        {data.note && <span className="block mt-1 text-gray-400">{data.note}</span>}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="text-xs text-gray-400 px-4 py-2">
        Showing {returned} of {total} results
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-left text-xs">
            <th className="px-4 py-2.5 font-medium">Code</th>
            <th className="px-4 py-2.5 font-medium">Description</th>
            <th className="px-4 py-2.5 font-medium">Rate</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r: any, i: number) => (
            <tr key={i} className={`border-t border-gray-100 ${r.is_leaf ? '' : 'bg-gray-50/50'}`}>
              <td className="px-4 py-2.5 font-mono text-teal-700 whitespace-nowrap text-xs">
                {r.hts_code}
                {r.is_leaf && <span className="ml-1.5 text-[10px] text-green-600 font-sans font-medium">leaf</span>}
              </td>
              <td className="px-4 py-2.5 text-gray-700 text-xs">{r.description}</td>
              <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap text-xs">{r.general_rate || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── CROSS rulings list ───────────────────────────────────────────────────── */

const CROSSResultsList: React.FC<{ data: any }> = ({ data }) => {
  const results = data.results || [];

  if (results.length === 0) {
    return (
      <div className="text-sm text-gray-500 px-4 py-3">
        No CROSS rulings found.
        {data.note && <span className="block mt-1 text-gray-400">{data.note}</span>}
      </div>
    );
  }

  return (
    <div className="space-y-2 px-4 py-2">
      <div className="text-xs text-gray-400">
        {data.total_results || results.length} ruling{results.length !== 1 ? 's' : ''} found
      </div>
      {results.map((r: any, i: number) => (
        <div key={i} className="rounded-lg border border-gray-100 px-3 py-2.5">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-sm font-medium text-teal-700">{r.ruling_number}</span>
            {r.date && <span className="text-xs text-gray-400">{r.date.split('T')[0]}</span>}
          </div>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">{r.subject}</p>
          {r.tariff_numbers?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {r.tariff_numbers.map((t: string, j: number) => (
                <span key={j} className="text-[10px] font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ── Content renderer ─────────────────────────────────────────────────────── */

const isHTSResult = (data: any): boolean =>
  data && typeof data === 'object' && Array.isArray(data.results) &&
  data.results.length > 0 && data.results[0]?.hts_code !== undefined;

const isCROSSResult = (data: any): boolean =>
  data && typeof data === 'object' && Array.isArray(data.results) &&
  data.results.length > 0 && data.results[0]?.ruling_number !== undefined;

const isNotesResult = (data: any): boolean =>
  data && typeof data === 'object' && Array.isArray(data.results) &&
  data.results.length > 0 && data.results[0]?.note_text !== undefined;

/* ── HTS Notes renderer ───────────────────────────────────────────────────── */

const HTSNotesPanel: React.FC<{ data: any }> = ({ data }) => {
  const results = data.results || [];

  return (
    <div className="space-y-3 px-4 py-3">
      <div className="text-xs text-gray-400">
        {results.length} note{results.length !== 1 ? 's' : ''}
      </div>
      {results.map((note: any, i: number) => (
        <div key={i} className="rounded-lg border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
              {note.note_type || 'Note'}
            </span>
            {note.scope_value && (
              <span className="text-[10px] text-gray-400">
                Chapter {note.scope_value}
                {note.note_number && `, Note ${note.note_number}`}
              </span>
            )}
            {note.revision_date && (
              <span className="text-[10px] text-gray-400 ml-auto">
                {note.revision_date}
              </span>
            )}
          </div>
          <div className="px-3 py-2.5 text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
            {note.note_text}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Generic structured data renderer ─────────────────────────────────────── */

const GenericDataPanel: React.FC<{ data: any }> = ({ data }) => {
  if (Array.isArray(data.results) && data.results.length > 0) {
    return <GenericResultsList results={data.results} meta={data} />;
  }
  if (Array.isArray(data)) {
    return <GenericResultsList results={data} />;
  }
  return <KeyValueCard data={data} />;
};

const GenericResultsList: React.FC<{ results: any[]; meta?: any }> = ({ results, meta }) => (
  <div className="space-y-2 px-4 py-3">
    {meta?.returned !== undefined && (
      <div className="text-xs text-gray-400 mb-1">
        {meta.returned} result{meta.returned !== 1 ? 's' : ''}
      </div>
    )}
    {results.map((item: any, i: number) => (
      <KeyValueCard key={i} data={item} />
    ))}
  </div>
);

const KeyValueCard: React.FC<{ data: any }> = ({ data }) => {
  if (!data || typeof data !== 'object') {
    return <div className="px-4 py-2 text-xs text-gray-600">{String(data)}</div>;
  }

  const entries = Object.entries(data).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <table className="w-full text-xs table-fixed">
        <colgroup>
          <col className="w-[130px]" />
          <col />
        </colgroup>
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-gray-50 last:border-0">
              <td className="px-3 py-2 text-gray-500 font-medium align-top break-words">
                {formatKey(key)}
              </td>
              <td className="px-3 py-2 text-gray-700 align-top break-words overflow-hidden">
                <CellValue value={value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CellValue: React.FC<{ value: any; depth?: number }> = ({ value, depth = 0 }) => {
  if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${value ? 'text-green-600' : 'text-gray-400'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">-</span>;
    if (value.every((v) => typeof v !== 'object')) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span key={i} className="inline-block bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[10px]">
              {humanizeToken(String(v))}
            </span>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-1.5 mt-0.5">
        {value.map((v, i) => (
          <div key={i} className="pl-2 border-l-2 border-gray-100">
            <CellValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return <span className="text-gray-400">-</span>;
    return (
      <div className={`space-y-2 ${depth > 0 ? '' : 'mt-0.5'}`}>
        {entries.map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] text-gray-400 font-medium mb-0.5">
              {formatKey(k)}
            </div>
            <div className="text-[11px] text-gray-700 pl-2 border-l-2 border-gray-100 break-words">
              <CellValue value={v} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  const str = String(value);
  if (str.length > 200) {
    return <span className="leading-relaxed whitespace-pre-wrap">{str}</span>;
  }
  return <span>{humanizeToken(str)}</span>;
};

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Title-case lowercase enum-like tokens (e.g. `full_determination` →
 * `Full Determination`, `deterministic` → `Deterministic`) while leaving codes,
 * country codes, dates, rates, and prose untouched. A token is a lowercase word
 * optionally joined by underscores — this deliberately skips anything with dots,
 * hyphens, spaces, digits-with-symbols, or uppercase letters (e.g. `CN`,
 * `6403.99.90`, `2026-06-06`, `10%`).
 */
function humanizeToken(value: string): string {
  return /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/.test(value) ? formatKey(value) : value;
}

/* ── Content dispatcher ───────────────────────────────────────────────────── */

const PanelContent: React.FC<{ item: EvidenceItem }> = ({ item }) => {
  const { data } = item;
  if (!data) return <div className="px-4 py-3 text-sm text-gray-400">No data</div>;
  if (isHTSResult(data)) return <HTSResultsTable data={data} />;
  if (isCROSSResult(data)) return <CROSSResultsList data={data} />;
  if (isNotesResult(data)) return <HTSNotesPanel data={data} />;
  return <GenericDataPanel data={data} />;
};

/* ── Main panel ───────────────────────────────────────────────────────────── */

const EvidencePanel: React.FC = () => {
  const { isOpen, items, activeIndex, closePanel, setActiveIndex } = useEvidencePanel();
  const activeItem = items[activeIndex];

  return (
    <div
      className={`h-full flex-shrink-0 border-l border-gray-200 bg-white flex flex-col transition-all duration-300 ease-in-out ${
        isOpen ? 'w-[420px] opacity-100' : 'w-0 opacity-0 overflow-hidden'
      }`}
    >
      {activeItem && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-800 truncate">
                {TOOL_LABELS[activeItem.tool] || formatKey(activeItem.tool || activeItem.label || '')}
              </span>
            </div>
            <button
              onClick={closePanel}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            <PanelContent item={activeItem} />
          </div>

          {/* Footer — item navigation */}
          {items.length > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => setActiveIndex(Math.max(0, activeIndex - 1))}
                disabled={activeIndex === 0}
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-[10px] text-gray-400 tabular-nums">
                {activeIndex + 1} / {items.length}
              </span>
              <button
                onClick={() => setActiveIndex(Math.min(items.length - 1, activeIndex + 1))}
                disabled={activeIndex === items.length - 1}
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EvidencePanel;
